import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, User as UserIcon, Camera, LockKey, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState(user?.phone || user?.owner_phone || "");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user) return null;
  if (!user.needs_onboarding) {
    // Se qualcuno naviga qui manualmente ma non ha bisogno di onboarding
    nav("/dashboard", { replace: true });
    return null;
  }

  // Iter54: solo i Master (senza parent_user_id) vedono il reminder di caricamento
  // del logo aziendale. I sub-user ereditano il logo del Master, quindi il blocco
  // viene nascosto per loro.
  const isSubUser = !!user.parent_user_id;
  const isProfessional = user.role === "professional";
  // Per i professionisti (Master e sub-user) il termine è "logo aziendale";
  // per gli altri ruoli resta "foto profilo".
  const photoLabel = isProfessional ? "logo" : "foto profilo";

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    if (pwd !== confirm) { toast.error("Le password non coincidono"); return; }
    if (!phone || phone.trim().length < 6) { toast.error("Inserisci un numero di telefono valido"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/complete-onboarding", { new_password: pwd, phone: phone.trim() });
      // Aggiorna user in memoria + localStorage
      const updated = r.data.user;
      localStorage.setItem("ht_user", JSON.stringify(updated));
      setUser(updated);
      toast.success("Benvenuto in Housetag! Setup completato.");
      // Segnala al Layout di mostrare il modal Passkey (trigger=onboarding, ignora dismiss di sessione)
      try { sessionStorage.setItem("ht_just_onboarded", "1"); } catch (_) { /* ignore */ }
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile completare l'onboarding");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[color:hsl(var(--surface))]" data-testid="onboarding-page">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg ht-gradient flex items-center justify-center text-white font-bold">H</div>
            <div>
              <div className="font-display text-xl font-bold text-purple-800">Housetag.it</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-widest">Primo accesso</div>
            </div>
          </div>

          <div className="mb-6">
            <div className="label-eyebrow text-purple-700">Benvenuto/a, {user.first_name || user.email}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Completa il tuo profilo</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Prima di iniziare, imposta una nuova password personale e conferma il tuo numero di telefono.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            {/* Nuova password */}
            <div>
              <Label className="flex items-center gap-2"><LockKey size={16} weight="duotone" /> Nuova password (min 8 caratteri)</Label>
              <div className="relative mt-1">
                <Input
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={8}
                  value={pwd}
                  onChange={e => setPwd(e.target.value)}
                  className="pr-10"
                  autoComplete="new-password"
                  data-testid="onb-pwd"
                />
                <button type="button" onClick={() => setShowPwd(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  data-testid="onb-toggle-pwd">
                  {showPwd ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>

            <div>
              <Label>Conferma nuova password</Label>
              <Input
                type={showPwd ? "text" : "password"}
                required
                minLength={8}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="mt-1"
                autoComplete="new-password"
                data-testid="onb-confirm"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2"><Phone size={16} weight="duotone" /> Numero di telefono</Label>
              <Input
                type="tel"
                required
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+39 333 1234567"
                className="mt-1"
                data-testid="onb-phone"
              />
            </div>

            {/* Reminder logo aziendale — nascosto per i sub-user (Iter54) */}
            {!isSubUser && (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="onb-avatar-reminder">
                <Camera size={22} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium text-amber-900">Ricorda di caricare il tuo {photoLabel}</div>
                  <div className="text-xs text-amber-800 mt-1">
                    Dopo aver completato questa pagina, vai in <strong>Impostazioni → Profilo</strong> per aggiungere il tuo {photoLabel}. Aiuta colleghi e clienti a riconoscerti nelle pratiche e nella chat.
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full ht-gradient border-0"
              data-testid="onb-submit"
            >
              {loading ? "Salvataggio…" : "Completa e accedi"}
            </Button>

            <button
              type="button"
              onClick={() => { logout(); nav("/login", { replace: true }); }}
              className="w-full text-xs text-muted-foreground hover:text-foreground pt-2"
              data-testid="onb-logout"
            >
              Esci senza completare
            </button>
          </form>
        </div>
      </div>

      {/* Right: welcome panel */}
      <div className="hidden md:flex items-center justify-center p-12 bg-gradient-to-br from-purple-800 to-purple-600 text-white">
        <div className="max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mb-6">
            <UserIcon size={32} weight="duotone" />
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight">Benvenuto nel team Housetag</h2>
          <p className="text-white/85 mt-4 text-sm leading-relaxed">
            Il tuo account è stato creato da un amministratore. Per la tua sicurezza,
            imposta subito una password personale e conferma i tuoi contatti. Una volta
            completato, potrai accedere a pratiche, bacheca personale e strumenti del tuo ruolo.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex items-center gap-3"><span className="w-2 h-2 rounded-full bg-white/70" /> Password cifrata solo lato server</li>
            <li className="flex items-center gap-3"><span className="w-2 h-2 rounded-full bg-white/70" /> Contatto diretto per notifiche di pratiche</li>
            <li className="flex items-center gap-3"><span className="w-2 h-2 rounded-full bg-white/70" /> {isProfessional ? "Logo aziendale" : "Foto profilo"} consigliato per la collaborazione</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
